import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { KeyRound, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { changePin } from '../../lib/api';

export default function ChangePinModal({ open, onOpenChange }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => { setCurrent(''); setNext(''); setConfirm(''); setShow(false); };

  const handleClose = (v) => { if (!v) reset(); onOpenChange(v); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(current) || !/^\d{4}$/.test(next)) {
      toast.error('El PIN debe ser de 4 dígitos numéricos');
      return;
    }
    if (next !== confirm) {
      toast.error('La confirmación del nuevo PIN no coincide');
      return;
    }
    if (next === current) {
      toast.error('El nuevo PIN debe ser distinto al actual');
      return;
    }
    setBusy(true);
    try {
      await changePin(current, next);
      toast.success('PIN actualizado correctamente');
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cambiar el PIN');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm" data-testid="change-pin-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-blue-600" />
            Cambiar PIN de administrador
          </DialogTitle>
          <DialogDescription>
            Por seguridad, tu PIN nunca se vuelve a sincronizar desde el sistema una vez que lo cambias aquí.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="current-pin">PIN actual</Label>
            <div className="relative mt-1">
              <Input
                id="current-pin"
                type={show ? 'text' : 'password'}
                inputMode="numeric"
                maxLength={4}
                value={current}
                onChange={(e) => setCurrent(e.target.value.replace(/\D/g, ''))}
                placeholder="••••"
                className="pr-10 text-center text-lg tracking-widest font-mono"
                autoFocus
                data-testid="current-pin-input"
              />
              <button
                type="button"
                onClick={() => setShow(!show)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label htmlFor="new-pin">Nuevo PIN (4 dígitos)</Label>
            <Input
              id="new-pin"
              type={show ? 'text' : 'password'}
              inputMode="numeric"
              maxLength={4}
              value={next}
              onChange={(e) => setNext(e.target.value.replace(/\D/g, ''))}
              placeholder="••••"
              className="mt-1 text-center text-lg tracking-widest font-mono"
              data-testid="new-pin-input"
            />
          </div>
          <div>
            <Label htmlFor="confirm-pin">Confirmar nuevo PIN</Label>
            <Input
              id="confirm-pin"
              type={show ? 'text' : 'password'}
              inputMode="numeric"
              maxLength={4}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ''))}
              placeholder="••••"
              className="mt-1 text-center text-lg tracking-widest font-mono"
              data-testid="confirm-pin-input"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={busy}>Cancelar</Button>
            <Button type="submit" disabled={busy || current.length !== 4 || next.length !== 4 || confirm.length !== 4} data-testid="confirm-change-pin-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cambiar PIN'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
